import { useEffect, useRef, useState } from 'react';
import type { SamplerZone } from '@/engine/instruments';
import { readEnvelope } from '@/assets/waveform';
import * as SamplerCommands from '@/commands/samplerCommands';
import { getServices } from '@/state/services';
import { useDaw } from '@/state/store';
import {
  envelopeRequest,
  fitRegion,
  fractionOfSeconds,
  panView,
  secondsAtFraction,
  showsWholeFile,
  wholeFile,
  zoomView,
  type ZoneView,
} from './zoneView';

/**
 * The sample behind a zone, with its region drawn on it.
 *
 * Setting a zone's start and end by typing seconds works, and is how this was
 * built first; it is also how nobody trims a sample. The part of a recording you
 * want is obvious the moment you can see the recording — where the silence
 * before the hit ends, where the tail stops being useful — and invisible when
 * the only evidence is two numbers.
 *
 * The waveform is drawn from the same mip pyramid the timeline uses, so a zone
 * over a ten-minute file costs no more to draw than one over a second of it.
 * It draws a *window* onto the file rather than the whole of it: at this width
 * a long recording is seconds a pixel, and a start point set on that is a
 * guess. Ctrl-wheel zooms around the pointer, wheel scrolls, and two buttons
 * jump to the zone's own region and back to the whole file. The window is
 * kept per zone, so switching between zones does not lose where each one was.
 */

const HEIGHT = 44;
const HANDLE_GRAB_PX = 6;

type Handle = 'start' | 'end' | 'loopStart' | 'loopEnd';

/** Whether a zone's loop points are anywhere but the region's own ends. */
function loopsInside(zone: SamplerZone): boolean {
  return zone.loopStartSec > zone.offsetSec || zone.loopEndSec < zone.offsetSec + zone.durationSec;
}
/** The strip along the bottom showing where the window sits in the file. */
const STRIP_HEIGHT = 3;

export function ZoneWaveform({ trackId, zone }: { trackId: string; zone: SamplerZone }): JSX.Element {
  const dispatch = useDaw((s) => s.dispatch);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(240);
  const [ready, setReady] = useState(0);
  const dragRef = useRef<Handle | null>(null);

  const assets = getServices().assets();
  const asset = useDaw((s) => s.project.assets[zone.assetId]);
  const totalSec = asset?.durationSec ?? 0;

  // The window starts as the whole file and follows the file if it changes.
  const [view, setView] = useState<ZoneView>(() => wholeFile(totalSec));
  useEffect(() => {
    setView(wholeFile(totalSec));
  }, [zone.assetId, totalSec]);

  // Peaks are fetched once and then read synchronously while drawing, exactly as
  // the timeline does it.
  useEffect(() => {
    if (!assets || assets.peekPeaks(zone.assetId)) {
      setReady((n) => n + 1);
      return;
    }
    let cancelled = false;
    void assets.getPeaks(zone.assetId).then(() => {
      if (!cancelled) setReady((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [assets, zone.assetId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new ResizeObserver(() => setWidth(Math.max(80, canvas.clientWidth)));
    observer.observe(canvas);
    setWidth(Math.max(80, canvas.clientWidth));
    return () => observer.disconnect();
  }, []);

  // React's synthetic wheel listener is passive, so it cannot keep the page
  // from scrolling under a zoom gesture; the listener is attached by hand.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || totalSec <= 0) return;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const fraction = (e.clientX - rect.left) / Math.max(1, rect.width);
      if (e.ctrlKey || e.metaKey) {
        setView((v) => zoomView(v, Math.exp(-e.deltaY / 300), fraction, totalSec));
      } else {
        const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        setView((v) => panView(v, delta / 600, totalSec));
      }
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [totalSec]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(HEIGHT * dpr);
    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, HEIGHT);
    ctx.fillStyle = '#0b0d11';
    ctx.fillRect(0, 0, width, HEIGHT);

    const peaks = assets?.peekPeaks(zone.assetId) ?? null;
    if (peaks && totalSec > 0) {
      const spanSec = view.toSec - view.fromSec;
      const request = envelopeRequest(view, width, peaks.sampleRate);
      const envelope = new Float32Array(width * 2);
      readEnvelope(peaks, 0, request.startSample, request.samplesPerPixel, width, envelope);

      const bodyHeight = HEIGHT - STRIP_HEIGHT;
      const middle = bodyHeight / 2;
      ctx.fillStyle = '#2b3a52';
      for (let x = 0; x < width; x++) {
        const min = envelope[x * 2];
        const max = envelope[x * 2 + 1];
        const top = middle - max * (bodyHeight / 2 - 2);
        const bottom = middle - min * (bodyHeight / 2 - 2);
        ctx.fillRect(x, top, 1, Math.max(1, bottom - top));
      }

      // The region the zone actually plays, drawn over the rest. Either edge
      // may be off screen when zoomed in; the shading still says which side
      // of the window is inside the zone.
      const from = fractionOfSeconds(view, zone.offsetSec) * width;
      const to = fractionOfSeconds(view, zone.offsetSec + zone.durationSec) * width;
      ctx.fillStyle = 'rgba(9, 11, 15, 0.6)';
      if (from > 0) ctx.fillRect(0, 0, Math.min(width, from), bodyHeight);
      if (to < width) ctx.fillRect(Math.max(0, to), 0, width, bodyHeight);

      ctx.fillStyle = '#7fb0ff';
      for (const x of [from, to]) {
        if (x >= -1 && x <= width + 1) ctx.fillRect(Math.round(x) - 0.5, 0, 1.5, bodyHeight);
      }

      // A looping zone's loop points, when they are not simply the region's
      // ends: the part that holds while the key is down, lit a little, with
      // its own two handles.
      if (zone.loop && loopsInside(zone)) {
        const loopFrom = fractionOfSeconds(view, zone.loopStartSec) * width;
        const loopTo = fractionOfSeconds(view, zone.loopEndSec) * width;
        ctx.fillStyle = 'rgba(245, 158, 11, 0.12)';
        ctx.fillRect(Math.max(0, loopFrom), 0, Math.min(width, loopTo) - Math.max(0, loopFrom), bodyHeight);
        ctx.fillStyle = '#f59e0b';
        for (const x of [loopFrom, loopTo]) {
          if (x >= -1 && x <= width + 1) ctx.fillRect(Math.round(x) - 0.5, 0, 1.5, bodyHeight);
        }
      }

      // Where the window sits in the whole file, as a strip along the bottom:
      // the one thing a zoomed view has to keep saying is how much is not shown.
      ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
      ctx.fillRect(0, bodyHeight, width, STRIP_HEIGHT);
      ctx.fillStyle = showsWholeFile(view, totalSec) ? 'rgba(255, 255, 255, 0.18)' : '#7fb0ff';
      ctx.fillRect((view.fromSec / totalSec) * width, bodyHeight, Math.max(2, (spanSec / totalSec) * width), STRIP_HEIGHT);
    } else {
      ctx.fillStyle = '#4b5563';
      ctx.font = '9px ui-monospace, monospace';
      ctx.fillText(peaks ? 'No audio' : 'Loading…', 6, HEIGHT / 2);
    }
    ctx.restore();
  }, [assets, zone.assetId, zone.offsetSec, zone.durationSec, zone.loop, zone.loopStartSec, zone.loopEndSec, totalSec, width, ready, view]);

  const secondsAt = (clientX: number, canvas: HTMLCanvasElement): number => {
    const rect = canvas.getBoundingClientRect();
    const fraction = (clientX - rect.left) / Math.max(1, rect.width);
    return Math.min(totalSec, Math.max(0, secondsAtFraction(view, fraction)));
  };

  const apply = (edge: Handle, seconds: number): void => {
    if (totalSec <= 0) return;
    if (edge === 'loopStart' || edge === 'loopEnd') {
      // The command keeps the points inside the region and in order; the
      // drag only says where the pointer is.
      dispatch({
        type: SamplerCommands.updateSamplerZone.type,
        params: { trackId, zoneId: zone.id, [edge === 'loopStart' ? 'loopStartSec' : 'loopEndSec']: seconds },
      });
      return;
    }
    if (edge === 'start') {
      // The end stays where it is: dragging the start of a region moves its
      // start, not the whole region.
      const end = zone.offsetSec + zone.durationSec;
      const offsetSec = Math.min(seconds, Math.max(0, end - 0.005));
      dispatch({
        type: SamplerCommands.updateSamplerZone.type,
        params: { trackId, zoneId: zone.id, offsetSec, durationSec: Math.max(0.005, end - offsetSec) },
      });
      return;
    }
    dispatch({
      type: SamplerCommands.updateSamplerZone.type,
      params: { trackId, zoneId: zone.id, durationSec: Math.max(0.005, seconds - zone.offsetSec) },
    });
  };

  const whole = showsWholeFile(view, totalSec);
  const windowLabel = whole ? 'whole file' : `${view.fromSec.toFixed(3)}–${view.toSec.toFixed(3)} s`;

  return (
    <div className="zone__view" style={{ gridColumn: '1 / -1' }}>
      <canvas
        ref={canvasRef}
        className="zone__waveform"
        style={{ height: HEIGHT }}
        aria-label={`${zone.name} waveform`}
        title={
          zone.loop
            ? 'Drag the edges to set where this zone starts and ends, and the amber marks to set where it loops; Alt-click puts a loop point down. Ctrl-wheel zooms around the pointer; wheel scrolls.'
            : 'Drag the edges to set where this zone starts and ends. Ctrl-wheel zooms around the pointer; wheel scrolls.'
        }
        onPointerDown={(e) => {
          if (totalSec <= 0) return;
          const canvas = e.currentTarget;
          const rect = canvas.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const startX = fractionOfSeconds(view, zone.offsetSec) * rect.width;
          const endX = fractionOfSeconds(view, zone.offsetSec + zone.durationSec) * rect.width;

          // Whichever handle is nearest, if any is near enough to have been
          // aimed at. A looping zone's loop points are handles too, and an
          // Alt-click away from every handle puts down whichever loop point
          // is nearer; otherwise the click is a new start, which is the common
          // case on a sample that begins with silence.
          const handles: [Handle, number][] = [
            ['start', startX],
            ['end', endX],
          ];
          if (zone.loop) {
            handles.push(['loopStart', fractionOfSeconds(view, zone.loopStartSec) * rect.width]);
            handles.push(['loopEnd', fractionOfSeconds(view, zone.loopEndSec) * rect.width]);
          }
          let edge: Handle | null = null;
          let nearest = HANDLE_GRAB_PX;
          for (const [handle, hx] of handles) {
            if (Math.abs(x - hx) <= nearest) {
              nearest = Math.abs(x - hx);
              edge = handle;
            }
          }
          if (!edge && zone.loop && e.altKey) {
            const midSec = (zone.loopStartSec + zone.loopEndSec) / 2;
            edge = secondsAt(e.clientX, canvas) < midSec ? 'loopStart' : 'loopEnd';
          }

          dragRef.current = edge ?? 'start';
          // The edit happens first and the capture is attempted afterwards: not
          // every environment implements pointer capture, and a failure to hold
          // the pointer must not swallow the edit that was already asked for.
          apply(dragRef.current, secondsAt(e.clientX, canvas));
          try {
            canvas.setPointerCapture(e.pointerId);
          } catch {
            /* the drag simply ends when the pointer leaves */
          }
        }}
        onPointerMove={(e) => {
          if (!dragRef.current) return;
          apply(dragRef.current, secondsAt(e.clientX, e.currentTarget));
        }}
        onPointerUp={(e) => {
          dragRef.current = null;
          try {
            e.currentTarget.releasePointerCapture(e.pointerId);
          } catch {
            /* nothing was captured */
          }
          getServices().commands.breakCoalescing();
        }}
      />
      <div className="zone__viewbar">
        <button
          type="button"
          className="chip"
          disabled={totalSec <= 0}
          aria-label={`Zoom ${zone.name} to its region`}
          title="Show just the part of the file this zone plays, with a little room either side"
          onClick={() => setView(fitRegion(zone.offsetSec, zone.durationSec, totalSec))}
        >
          ZONE
        </button>
        <button
          type="button"
          className="chip"
          disabled={whole}
          aria-label={`Show the whole file for ${zone.name}`}
          title="Show the whole file again"
          onClick={() => setView(wholeFile(totalSec))}
        >
          ALL
        </button>
        {/*
          The region as numbers, because a start set on a zoomed waveform is a
          precise thing and deserves to be read back precisely; the window
          beside it says how much of the file is on screen.
        */}
        <span
          className="inspector__readout"
          aria-label={`${zone.name} region`}
          title="Where this zone starts and ends in the file, in seconds"
          style={{ marginLeft: 'auto' }}
        >
          {zone.offsetSec.toFixed(3)}–{(zone.offsetSec + zone.durationSec).toFixed(3)} s
        </span>
        <span
          className="inspector__readout"
          aria-label={`${zone.name} window`}
          title="How much of the file the waveform is showing"
          style={{ opacity: 0.7 }}
        >
          {windowLabel}
        </span>
      </div>
    </div>
  );
}
